import { call, put, select, take, takeEvery } from 'redux-saga/effects';
import { api } from 'services';
import { notificationContainer } from 'utils/notificationContainer';
import { getErrorMessage } from 'utils/apiError';
import { PayloadAction } from '@reduxjs/toolkit';
import fileDownload from 'js-file-download';

import {
	TInvoicesRequest,
	TInvoicesResponse,
	TFetchPaymentLinkRequestPayload,
	TFetchPaymentLinkResponsePayload,
	TDownloadInvoicePdfRequestPayload,
	TDownloadInvoicePdfResponsePayload,
	TDownloadInvoiceCsvRequestPayload,
	TDownloadInvoiceCsvResponsePayload,
	TInvoicesResponsePayload,
	TLastInvoiceStatusResponse,
} from './types';
import {
	getInvoicesRequest,
	getInvoicesSuccess,
	getPaymentLinkRequest,
	getPaymentLinkSuccess,
	downloadInvoicePdfRequest,
	downloadInvoicePdfSuccess,
	downloadInvoiceCsvRequest,
	downloadInvoiceCsvSuccess,
	setDownloadedInvoicePdf,
	setDownloadedInvoiceCsv,
	getLastInvoiceStatusRequest,
	getLastInvoiceStatusSuccess,
} from './reducer';
import { getInvoices } from './selectors';
import { refreshTokenByTimeOutRequest, refreshTokenByTimeOutSuccess } from '../auth/reducer';

function* getInvoicesWorker(action: PayloadAction<TInvoicesRequest>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TInvoicesResponse = yield call(api.invoices.getInvoices, payload);
		yield put(getInvoicesSuccess(response));
	} catch (error) {
		// notificationContainer('Error', error.message);
	}
}

function* getPaymentLinkWorker(action: PayloadAction<TFetchPaymentLinkRequestPayload>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TFetchPaymentLinkResponsePayload = yield call(
			api.invoices.getPaymentLink,
			payload,
		);
		yield put(getPaymentLinkSuccess(response));
	} catch (error) {
		// ends the loading: the invoices page closes the window it opened for the payment page
		yield put(getPaymentLinkSuccess(null));
		notificationContainer(getErrorMessage(error), 'error');
	}
}

function* downloadInvoicePdfWorker(action: PayloadAction<TDownloadInvoicePdfRequestPayload>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TDownloadInvoicePdfResponsePayload = yield call(
			api.invoices.getDownloadInvoicePdf,
			payload,
		);
		const invoices: TInvoicesResponsePayload = yield select(getInvoices);
		yield put(downloadInvoicePdfSuccess(response));
		if (response) {
			yield fileDownload(
				response,
				`invoice_${
					invoices?.data?.find((item) => item.id === payload.invoiceId)?.invoiceNumber ?? ''
				}.pdf`,
			);
		}
		yield put(setDownloadedInvoicePdf());
	} catch (error) {
		// notificationContainer('Error', error.message);
	}
}

function* downloadInvoiceCsvWorker(action: PayloadAction<TDownloadInvoiceCsvRequestPayload>) {
	const { payload } = action;
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TDownloadInvoiceCsvResponsePayload = yield call(
			api.invoices.getDownloadInvoiceCsv,
			payload,
		);
		const invoices: TInvoicesResponsePayload = yield select(getInvoices);

		yield put(downloadInvoiceCsvSuccess(response));
		if (response) {
			yield fileDownload(
				response,
				`invoice_${
					invoices?.data?.find((item) => item.id === payload.invoiceId)?.invoiceNumber ?? ''
				}_breakdown.zip`,
			);
		}
		yield put(setDownloadedInvoiceCsv());
	} catch (error) {
		// notificationContainer('Error', error.message);
	}
}

function* getLastInvoiceStatusWorker() {
	try {
		yield put(refreshTokenByTimeOutRequest());
		yield take(refreshTokenByTimeOutSuccess.type);

		const response: TLastInvoiceStatusResponse = yield call(api.invoices.getLastInvoiceStatus);
		yield put(getLastInvoiceStatusSuccess(response));
	} catch (error) {
		// notificationContainer('Error', error.message);
	}
}

export function* invoicesSaga() {
	yield takeEvery(getInvoicesRequest.type, getInvoicesWorker);
	yield takeEvery(getPaymentLinkRequest.type, getPaymentLinkWorker);
	yield takeEvery(downloadInvoicePdfRequest.type, downloadInvoicePdfWorker);
	yield takeEvery(downloadInvoiceCsvRequest.type, downloadInvoiceCsvWorker);
	yield takeEvery(getLastInvoiceStatusRequest.type, getLastInvoiceStatusWorker);
}
